import RiotControl from 'riotcontrol'
import logger from '../logger'
import extendedObservable from './base'
import api from '../api'
import shippingIds from '../maps/shippingIds'
import {
  get,
  merge,
} from 'lodash-es'


let trimmedAddresses

const shipMethods = (item, orderModel) => {
  let methods = []
  const fulfillmentData = orderModel.safeGet(`getters.fulfillmentsModel[${item.reiRefId}]`)

  if (item.membership || (item.giftCertificate && !item.certificateInfo.emailGiftCertificate)) {
    methods = [shippingIds.GIFT_CARD_AND_MEMBERSHIP]
  } else if (fulfillmentData) {
    methods = get(fulfillmentData, 'fulfillments["0"].shipMethodsInformation.allowedShipMethods', [])
      .map((method => shippingIds[method.id]))
  }

  // If there are no valid ship methods sending an empty array will cause the service to return
  // error so we want to send `NOT_APPLICABLE` at a minimum.
  if (!methods.length) methods = [shippingIds.NOT_APPLICABLE]

  return methods
}

const mapShipTo = (item, orderModel) => {
  const pickup = /PICKUP/i
  if (pickup.test(get(item, 'shipMethod.fulfillmentType', ''))) {
    return {
      addressLine1: get(item, 'shippingAddress.address.line1'),
      addressLine2: get(item, 'shippingAddress.address.line2') || null,
      addressLine3: null,
      city: get(item, 'shippingAddress.address.city'),
      stateCode: get(item, 'shippingAddress.address.stateCode'),
      zipCode: get(item, 'shippingAddress.address.postalCode'),
      countryCode: get(item, 'shippingAddress.address.countryCode'),
      storeId: get(item, 'shippingAddress.address.storeId'),
    }
  }

  return {
    addressLine1: get(item, 'shippingAddress.address.line1') || orderModel.safeGet('billingAddress.address.line1'),
    addressLine2: get(item, 'shippingAddress.address.line2') || orderModel.safeGet('billingAddress.address.line2') || null,
    addressLine3: null,
    city: get(item, 'shippingAddress.address.city') || orderModel.safeGet('billingAddress.address.city'),
    stateCode: orderModel.getters.shipState,
    zipCode: get(item, 'shippingAddress.address.postalCode') || orderModel.safeGet('billingAddress.address.postalCode'),
    countryCode: get(item, 'shippingAddress.address.countryCode') || orderModel.safeGet('billingAddress.address.countryCode'),
  }
}


export function ShippingChargesObservable() {
  const orderResourceKey = 'shipCharges'

  extendedObservable(this, 'shippingChargesModel')

  // successHandler for successful communication with the retail store service
  const successHandler = (json) => {
    logger('SUCCESS :: Communicated with Shipping Charge Service', 'info')()
    logger(json, 'debug')()

    // Reset ship groups
    this.model.shippingGroups = []

    this.model = merge(this.model, json)
    RiotControl.trigger('updateOrderResource', orderResourceKey, this.model)
    RiotControl.trigger('fetchingShippingChargeComplete')
    RiotControl.trigger('shippingChargeSuccess')
    return json
  }

  // errorHandler for unsuccessful communication with the retail store service
  const errorHandler = (xhr) => {
    const errorThrown = xhr.statusText
    const status = xhr.status
    logger(`ERROR :: (XHR) Failed to communicate with Shipping Charge Service ${errorThrown}`, 'error')()
    logger(`Status: ${status}`, 'error')()
    logger(xhr, 'error')()
    RiotControl.trigger('fetchingShippingChargeComplete')
    RiotControl.trigger('shippingChargeFailure', errorThrown)
  }

  // register and unregister event listeners
  this.on('registerShippingChargesObserver', observer => this.registerObserver(observer))
  this.on('unregisterShippingChargesObserver', observer => this.unregisterObserver(observer))

  this.on('getShippingCharges', (data, cb) => {
    const items = get(data, 'lineItems', this.orderModel.safeGet('lineItems', []))
    const orderModel = get(data, 'orderModel', this.orderModel)

    const payload = {
      orderId: orderModel.safeGet('externalOrderId'),
      orderChannel: 'ONLINE',
      customerType: orderModel.safeGet('customerInfo.customerType'),
      orderLines: items.map((item) => {
        const mapped = {
          id: item.reiRefId,
          lineSegment: 1,
          extnWebLineId: 0,
          skuInformation: {
            skuNumber: item.sku,
          },
          quantity: item.quantity,
          totalPrice: item.price.total,
          totalAdjustedPrice: item.price.totalAdjusted,
          shipTo: mapShipTo(item, orderModel),
          shipMethods: shipMethods(item, orderModel),
        }

        return mapped
      }),
    }

    RiotControl.trigger('fetchingShippingCharge')
    // Shipping Charges Web Service does not accept address lines larger than 30 characters.
    // One of REI's stores contains 31 characters. Spoke with Lei and the workaround is to trim
    // the address lines if there is a store id.
    api.shippingCharge.post(JSON.stringify(trimStoreAddresses(payload)))
      .then(restoreStoreAddresses(payload))
      .then(successHandler)
      .then(this.notifyObservers)
      .then(cb)
      .fail(errorHandler)
  })
}

const trimStoreAddresses = (payload) => {
  trimmedAddresses = {}
  const orderLines = get(payload, 'orderLines', [])
  orderLines.forEach((lineItem) => {
    if (get(lineItem, 'shipTo.storeId', false)) {
      if (get(lineItem, 'shipTo.addressLine1', '').length > 30) {
        trimmedAddresses[lineItem.id] = lineItem.shipTo.addressLine1
        // eslint-disable-next-line no-param-reassign
        lineItem.shipTo.addressLine1 = lineItem.shipTo.addressLine1.slice(0, 29)
      }
    }
  })
  return payload
}

const restoreStoreAddresses = (payload) => {
  if (trimmedAddresses) {
    const trimmedAddressKeys = Object.keys(trimmedAddresses)
    if ((trimmedAddressKeys.length > 0) && payload && payload.orderLines) {
      trimmedAddressKeys.forEach((key) => {
        const lineItem = payload.orderLines.find(item => item.id === key)
        if (lineItem && lineItem.shipTo) {
          lineItem.shipTo.addressLine1 = trimmedAddresses[key]
        }
      })
    }
  }
  trimmedAddresses = null
  return payload
}
